import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Theme } from "@mui/material/styles";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { DataGrid, GridColDef, GridRenderCellParams, useGridApiRef } from "@mui/x-data-grid";
import { DatePicker } from "@mui/x-date-pickers";
import moment, { Moment } from "moment";
import { toast } from "react-toastify";
import shiftManagementService from "services/shiftManagementService";
import { SAVE_BUTTON_SX } from "shifts/shiftStyles";
import { READ_ONLY_MESSAGE, useShiftPermissions } from "shifts/permissions";
import {
  ShiftDriverOption,
  ShiftGridRow,
  ShiftHubOption,
  ShiftLookupsResponse,
  ShiftReasonOption,
  ShiftSaveRow,
  ShiftVehicleOption,
} from "shifts/models";

// Yard Shift Update — the Flow port of the legacy Dashboard screen where shift data gets
// fixed before it goes to payroll and invoicing. Pick a location and date, correct the
// driver/vehicle/times/odometers, then approve or cancel.
//
// Rows go read-only once approved or canceled, exactly as the legacy grid did; reversing
// either is a power-user action that still lives in the Dashboard.

const FILTER_STORAGE_KEY = "shiftUpdateFilters";
const TIME_PATTERN = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/;
const DATE_PATTERN = /^\d{1,2}\/\d{1,2}\/\d{4}$/;

// The grid runs much wider than the screen, so the columns that tell you *whose* shift a row
// is — Shift ID, Driver and Vehicle — are frozen to the left edge; scrolling out to the
// odometer and approval columns used to leave you editing an anonymous row.
//
// Column pinning is a DataGrid Pro feature and we are on the community build, so this is the
// CSS equivalent, and it has two halves. Body cells are `position: sticky` against the virtual
// scroller. Headers cannot be: the grid scrolls the header row by transforming its container,
// which moves a sticky header right along with it, so those are counter-translated from the
// scroll handler instead (see pinHeaders below).
const PINNED_COLUMNS = [
  { field: "yardDailyId", width: 90 },
  { field: "driverId", width: 180 },
  { field: "vehicleId", width: 110 },
];

// A hard edge where the frozen block ends, so it reads as a separate pane once the columns
// underneath start sliding beneath it.
const PINNED_EDGE = { borderRight: "2px solid", borderRightColor: "divider" };

const pinnedColumnSx = PINNED_COLUMNS.reduce<Record<string, object>>((sx, col, i) => {
  const left = PINNED_COLUMNS.slice(0, i).reduce((sum, c) => sum + c.width, 0);
  const edge = i === PINNED_COLUMNS.length - 1 ? PINNED_EDGE : {};
  // Opaque backgrounds: the scrolled columns pass underneath these cells.
  sx[`& .MuiDataGrid-cell[data-field="${col.field}"]`] = {
    position: "sticky",
    left,
    zIndex: 2,
    backgroundColor: "background.paper",
    ...edge,
  };
  sx[`& .MuiDataGrid-columnHeader[data-field="${col.field}"]`] = {
    zIndex: 4,
    backgroundColor: "background.paper",
    ...edge,
  };
  // The row hover tint is translucent and paints on the row, which an opaque pinned cell
  // covers up — the hovered row would break in half at the frozen edge. Layering the same
  // tint as a gradient image keeps the cell opaque while picking the hover colour up.
  sx[`& .MuiDataGrid-row:hover .MuiDataGrid-cell[data-field="${col.field}"], & .MuiDataGrid-row.Mui-hovered .MuiDataGrid-cell[data-field="${col.field}"]`] =
    {
      backgroundImage: (theme: Theme) => `linear-gradient(${theme.palette.action.hover}, ${theme.palette.action.hover})`,
    };
  return sx;
}, {});

interface Filters {
  hubId: number | "";
  shiftDate: string;
  sortBy: string;
  includeApproved: boolean;
  includeCanceled: boolean;
}

const defaultFilters: Filters = {
  hubId: "",
  shiftDate: moment().format("YYYY-MM-DD"),
  sortBy: "Start Time",
  includeApproved: false,
  includeCanceled: false,
};

const ShiftUpdate = () => {
  // Field managers and viewers reach this screen deliberately -- they read it to see what is
  // still outstanding for payroll at their locations -- but nothing on it is theirs to change.
  const { canEditShiftUpdate, canApproveShift } = useShiftPermissions();
  const canSaveAnything = canEditShiftUpdate || canApproveShift;
  const [hubs, setHubs] = useState<ShiftHubOption[]>([]);
  const [filters, setFilters] = useState<Filters>(() => {
    const persisted = localStorage.getItem(FILTER_STORAGE_KEY);
    if (!persisted) return defaultFilters;
    try {
      // The date is deliberately not restored — a closer working today shouldn't land on
      // last week's shifts because that's what they looked at last.
      return { ...defaultFilters, ...JSON.parse(persisted), shiftDate: defaultFilters.shiftDate };
    } catch {
      return defaultFilters;
    }
  });

  const [rows, setRows] = useState<ShiftGridRow[]>([]);
  const [dirtyIds, setDirtyIds] = useState<Set<number>>(new Set());
  const [timeZone, setTimeZone] = useState("");
  const [missingClient, setMissingClient] = useState<number[]>([]);
  const [lookups, setLookups] = useState<ShiftLookupsResponse>({ drivers: [], vehicles: [], cancelReasons: [] });
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const gridRef = useGridApiRef();

  // Hold the pinned headers over their columns. The body cells manage this themselves with
  // `position: sticky`, but the header row is scrolled by a transform on its container, which
  // carries a sticky header along with it — so each pinned header is shifted back by the
  // current scroll offset. Written straight to the DOM: a state update per scroll tick would
  // re-render every cell in the grid.
  useEffect(() => {
    const api = gridRef.current;
    if (!api?.subscribeEvent) return;

    const pinHeaders = (left: number) => {
      const root = api.rootElementRef?.current;
      if (!root) return;
      PINNED_COLUMNS.forEach((col) => {
        const header = root.querySelector<HTMLElement>(`.MuiDataGrid-columnHeader[data-field="${col.field}"]`);
        if (header) header.style.transform = `translateX(${left}px)`;
      });
    };

    pinHeaders(api.getScrollPosition?.().left ?? 0);
    return api.subscribeEvent("scrollPositionChange", (params: { left: number }) => pinHeaders(params.left));
  });

  useEffect(() => {
    shiftManagementService
      .getHubs()
      .then(setHubs)
      .catch(() => toast.error("Could not load locations."));
  }, []);

  useEffect(() => {
    localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filters));
  }, [filters]);

  const loadShifts = useCallback(async () => {
    if (filters.hubId === "") {
      toast.warn("Pick a location first.");
      return;
    }

    setIsLoading(true);
    setDirtyIds(new Set());
    try {
      const [grid, lookupData] = await Promise.all([
        shiftManagementService.getShiftGrid({
          hubId: Number(filters.hubId),
          shiftDate: filters.shiftDate,
          sortBy: filters.sortBy,
          includeApproved: filters.includeApproved,
          includeCanceled: filters.includeCanceled,
        }),
        shiftManagementService.getLookups({ hubId: Number(filters.hubId), shiftDate: filters.shiftDate }),
      ]);

      setRows(grid.shifts ?? []);
      setTimeZone(grid.timeZone ?? "");
      setMissingClient(grid.shiftsMissingClient ?? []);
      setLookups(lookupData);
      setHasSearched(true);
    } catch {
      toast.error("Could not load shifts.");
    } finally {
      setIsLoading(false);
    }
  }, [filters]);

  /** Applies one field edit and marks the shift dirty so it gets included in the next save. */
  const updateRow = useCallback((yardDailyId: number, changes: Partial<ShiftGridRow>) => {
    setRows((current) =>
      current.map((row) => {
        if (row.yardDailyId !== yardDailyId) return row;

        const next = { ...row, ...changes };
        // Miles and the timecard are derived, so keep them honest as the closer types.
        next.miles = (next.odometerEnd ?? 0) - (next.odometerBeg ?? 0);
        next.timecardHours = calculateTimecard(next);
        return next;
      })
    );
    setDirtyIds((current) => new Set(current).add(yardDailyId));
  }, []);

  const validationError = useMemo(() => {
    for (const row of rows) {
      if (!dirtyIds.has(row.yardDailyId)) continue;

      const times: [string, string][] = [
        ["Invoice start", row.timeStartInvoice],
        ["Invoice end", row.timeEndInvoice],
        ["Payroll start", row.timeStartPayroll],
        ["Payroll end", row.timeEndPayroll],
      ];
      for (const [label, value] of times) {
        if (value && !TIME_PATTERN.test(value)) {
          return `Shift ${row.yardDailyId}: ${label} time must be HH:mm.`;
        }
      }

      const dates: [string, string][] = [
        ["Invoice start", row.dateStartInvoice],
        ["Invoice end", row.dateEndInvoice],
        ["Payroll start", row.dateStartPayroll],
        ["Payroll end", row.dateEndPayroll],
      ];
      for (const [label, value] of dates) {
        if (value && !DATE_PATTERN.test(value)) {
          return `Shift ${row.yardDailyId}: ${label} date must be MM/DD/YYYY.`;
        }
      }

      if (row.isCanceled && !row.cancelReason) {
        return `Shift ${row.yardDailyId}: a canceled shift needs a reason.`;
      }
    }
    return "";
  }, [rows, dirtyIds]);

  const handleSave = async () => {
    if (dirtyIds.size === 0) {
      toast.info("Nothing has changed.");
      return;
    }
    if (validationError) {
      toast.error(validationError);
      return;
    }

    setIsSaving(true);
    try {
      const payload: ShiftSaveRow[] = rows
        .filter((row) => dirtyIds.has(row.yardDailyId))
        .map((row) => ({
          yardDailyId: row.yardDailyId,
          driverId: row.driverId,
          vehicleId: row.vehicleId,
          controlNumber: row.controlNumber,
          dateStartInvoice: row.dateStartInvoice,
          timeStartInvoice: row.timeStartInvoice,
          dateEndInvoice: row.dateEndInvoice,
          timeEndInvoice: row.timeEndInvoice,
          dateStartPayroll: row.dateStartPayroll,
          timeStartPayroll: row.timeStartPayroll,
          dateEndPayroll: row.dateEndPayroll,
          timeEndPayroll: row.timeEndPayroll,
          tripsRan: row.tripsRan,
          odometerBeg: row.odometerBeg,
          odometerEnd: row.odometerEnd,
          approved: row.approved,
          isCanceled: row.isCanceled,
          cancelReason: row.cancelReason,
          notes: row.notes,
          shiftDateAdjust: row.shiftDateAdjust,
        }));

      const result = await shiftManagementService.saveShifts({ hubId: Number(filters.hubId), shifts: payload });

      if (result.success) {
        toast.success(result.message || "Shifts saved.");
      } else {
        toast.error(result.message || "Could not save shifts.");
      }
      result.errors?.forEach((error) => toast.error(`Shift ${error.yardDailyId}: ${error.message}`));

      // Re-read so approvals flip rows read-only and any server-side derivation shows up.
      await loadShifts();
    } catch {
      toast.error("Could not save shifts.");
    } finally {
      setIsSaving(false);
    }
  };

  const columns = useMemo(
    () => buildColumns(lookups, updateRow, { canEdit: canEditShiftUpdate, canApprove: canApproveShift }),
    [lookups, updateRow, canEditShiftUpdate, canApproveShift]
  );

  return (
    <Box sx={{ px: 1 }}>
      <Typography variant="h5" sx={{ mb: 1 }}>
        Shift Update
      </Typography>

      {!canSaveAnything && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {READ_ONLY_MESSAGE}
        </Alert>
      )}

      {canEditShiftUpdate && !canApproveShift && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You can correct shift data here, but approving a shift is restricted to administrators.
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel id="shift-location-label">Location</InputLabel>
            <Select
              labelId="shift-location-label"
              label="Location"
              value={filters.hubId}
              onChange={(e) => setFilters({ ...filters, hubId: e.target.value as number })}
            >
              {hubs.map((hub) => (
                <MenuItem key={hub.hubId} value={hub.hubId}>
                  {hub.location}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <DatePicker
            label="Date"
            value={moment(filters.shiftDate)}
            onChange={(value: Moment | null) =>
              setFilters({ ...filters, shiftDate: (value ?? moment()).format("YYYY-MM-DD") })
            }
            slotProps={{ textField: { size: "small", sx: { width: 170 } } }}
          />

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel id="shift-approved-label">Approved shifts</InputLabel>
            <Select
              labelId="shift-approved-label"
              label="Approved shifts"
              value={filters.includeApproved ? "Yes" : "No"}
              onChange={(e) => setFilters({ ...filters, includeApproved: e.target.value === "Yes" })}
            >
              <MenuItem value="No">No</MenuItem>
              <MenuItem value="Yes">Yes</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel id="shift-canceled-label">Canceled shifts</InputLabel>
            <Select
              labelId="shift-canceled-label"
              label="Canceled shifts"
              value={filters.includeCanceled ? "Yes" : "No"}
              onChange={(e) => setFilters({ ...filters, includeCanceled: e.target.value === "Yes" })}
            >
              <MenuItem value="No">No</MenuItem>
              <MenuItem value="Yes">Yes</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="shift-sort-label">Sort by</InputLabel>
            <Select
              labelId="shift-sort-label"
              label="Sort by"
              value={filters.sortBy}
              onChange={(e) => setFilters({ ...filters, sortBy: e.target.value })}
            >
              <MenuItem value="Start Time">Start Time</MenuItem>
              <MenuItem value="Shift Name">Shift Name</MenuItem>
            </Select>
          </FormControl>

          <Button variant="contained" color="info" onClick={loadShifts} disabled={isLoading}>
            Get Details
          </Button>

          {canSaveAnything && (
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={isSaving || dirtyIds.size === 0}
              sx={SAVE_BUTTON_SX}
            >
              {isSaving ? "Saving…" : `Apply Changes${dirtyIds.size ? ` (${dirtyIds.size})` : ""}`}
            </Button>
          )}
        </Stack>

        {timeZone && (
          <Typography variant="body2" sx={{ mt: 1.5, color: timeZone === "CENTRAL" ? "info.main" : "error.main" }}>
            These shifts are in {timeZone} time zone
          </Typography>
        )}
      </Paper>

      {missingClient.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Shift {missingClient.join(", ")} is not assigned to a client. Do not close it until that is fixed.
        </Alert>
      )}

      {validationError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {validationError}
        </Alert>
      )}

      <Paper sx={{ height: "calc(100vh - 320px)", minHeight: 400 }}>
        <DataGrid
          apiRef={gridRef as any}
          rows={rows}
          columns={columns}
          getRowId={(row) => row.yardDailyId}
          loading={isLoading}
          density="compact"
          rowHeight={72}
          columnHeaderHeight={56}
          disableRowSelectionOnClick
          disableColumnMenu
          hideFooterSelectedRowCount
          pageSizeOptions={[25, 50, 100]}
          initialState={{ pagination: { paginationModel: { pageSize: 50, page: 0 } } }}
          // All columns stay mounted so the pinned ones are never virtualized away when you
          // scroll right, and so the render zone keeps its horizontal offset at zero — sticky
          // cells would otherwise be pushed off by that offset.
          columnBuffer={columns.length}
          sx={{ fontSize: ".85em", ...pinnedColumnSx }}
          getRowClassName={(params) => (params.row.isEditable ? "" : "Mui-odd")}
          slots={{
            noRowsOverlay: () => (
              <Stack height="100%" alignItems="center" justifyContent="center">
                {hasSearched ? "No records found." : "Pick a location and date, then Get Details."}
              </Stack>
            ),
          }}
        />
      </Paper>
    </Box>
  );
};

/** Payroll end minus payroll start, in hours — mirrors the legacy in-grid calculation. */
const calculateTimecard = (row: ShiftGridRow): number => {
  const start = moment(`${row.dateStartPayroll} ${row.timeStartPayroll}`, "MM/DD/YYYY HH:mm", true);
  const end = moment(`${row.dateEndPayroll} ${row.timeEndPayroll}`, "MM/DD/YYYY HH:mm", true);
  if (!start.isValid() || !end.isValid()) return 0;
  return Math.round(end.diff(start, "hours", true) * 100) / 100;
};

type UpdateRow = (yardDailyId: number, changes: Partial<ShiftGridRow>) => void;

interface EditableTextCellProps {
  value: string;
  disabled: boolean;
  width: number;
  numeric?: boolean;
  onCommit: (next: string) => void;
}

/**
 * Text input for the grid's editable cells.
 *
 * The draft is held locally and only pushed up on blur. Committing per keystroke would
 * replace the rows array, re-render the grid and remount this input mid-word, so characters
 * were being dropped. Deferring to blur also matches the legacy screen, which recalculated
 * miles and the timecard on `onchange` rather than on every key.
 */
const EditableTextCell = ({ value, disabled, width, numeric = false, onCommit }: EditableTextCellProps) => {
  const [draft, setDraft] = useState(value ?? "");

  // Re-sync when the value changes underneath us — a reload after save, or a derived field.
  useEffect(() => {
    setDraft(value ?? "");
  }, [value]);

  return (
    <TextField
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        if (draft !== (value ?? "")) onCommit(draft);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") (e.target as HTMLInputElement).blur();
      }}
      disabled={disabled}
      size="small"
      variant="standard"
      inputProps={{
        style: { fontSize: ".85em", textAlign: numeric ? "right" : "left", padding: "2px 0" },
        inputMode: numeric ? "numeric" : "text",
      }}
      sx={{ width }}
    />
  );
};

/** What the signed-in user may do to the grid, as far as the columns are concerned. */
interface GridPermissions {
  canEdit: boolean;
  canApprove: boolean;
}

const buildColumns = (
  lookups: ShiftLookupsResponse,
  updateRow: UpdateRow,
  permissions: GridPermissions
): GridColDef[] => {
  /**
   * A cell is editable only when the shift itself is still open (the server's `isEditable`,
   * false once approved or canceled) *and* the user holds the edit permission. Both gates
   * disable the same inputs, so a manager reading the screen sees exactly the layout an
   * admin does, with nothing to type into.
   */
  const isRowEditable = (row: ShiftGridRow) => row.isEditable && permissions.canEdit;

  /** Small text input used for every free-text / numeric cell. */
  const textCell = (
    row: ShiftGridRow,
    value: string,
    onChange: (next: string) => void,
    width: number,
    numeric = false
  ) => (
    <EditableTextCell
      value={value}
      disabled={!isRowEditable(row)}
      width={width}
      numeric={numeric}
      onCommit={onChange}
    />
  );

  /** The stacked "entered value over source value" layout the legacy grid used. */
  const withSource = (row: ShiftGridRow, editor: JSX.Element, sourceValue: string | number) => (
    <Stack sx={{ width: "100%" }}>
      {editor}
      {row.hasSourceData && (
        <>
          <Box sx={{ borderTop: "1px solid", borderColor: "divider", my: 0.4 }} />
          <Typography variant="caption" sx={{ color: "text.secondary", textAlign: "right" }}>
            {sourceValue}
          </Typography>
        </>
      )}
    </Stack>
  );

  return [
    {
      field: "yardDailyId",
      headerName: "Shift ID",
      width: PINNED_COLUMNS[0].width,
      sortable: false,
      renderCell: (params: GridRenderCellParams<ShiftGridRow>) => (
        <Link to={`/shifts/dashboard/update/${params.row.yardDailyId}/events`}>{params.row.yardDailyId}</Link>
      ),
    },
    {
      field: "driverId",
      headerName: "Driver",
      width: PINNED_COLUMNS[1].width,
      sortable: false,
      renderCell: (params) => {
        const row = params.row as ShiftGridRow;
        // A driver assigned before termination may not be in the active list; keep them
        // selectable so the row doesn't silently reassign on save.
        const options: ShiftDriverOption[] = lookups.drivers.some((d) => d.driverId === row.driverId)
          ? lookups.drivers
          : [{ driverId: row.driverId, driverName: row.driverName || "(current)", isActive: true }, ...lookups.drivers];

        return (
          <Select
            value={row.driverId || 0}
            onChange={(e) => {
              const driverId = Number(e.target.value);
              const picked = options.find((d) => d.driverId === driverId);
              if (picked && !picked.isActive) {
                toast.warn(
                  `${picked.driverName.toUpperCase()} is an INACTIVE driver. Only pick them if they were recently terminated and still need paying.`
                );
              }
              updateRow(row.yardDailyId, { driverId, driverName: picked?.driverName ?? "" });
            }}
            disabled={!isRowEditable(row)}
            variant="standard"
            sx={{ width: "100%", fontSize: ".85em" }}
          >
            <MenuItem value={0}>(none)</MenuItem>
            {options.map((driver) => (
              <MenuItem key={driver.driverId} value={driver.driverId} sx={{ fontSize: ".85em" }}>
                {driver.driverName}
                {!driver.isActive ? " (inactive)" : ""}
              </MenuItem>
            ))}
          </Select>
        );
      },
    },
    {
      field: "vehicleId",
      headerName: "Vehicle",
      width: PINNED_COLUMNS[2].width,
      sortable: false,
      renderCell: (params) => {
        const row = params.row as ShiftGridRow;
        const options: ShiftVehicleOption[] = lookups.vehicles.some((v) => v.vehicleId === row.vehicleId)
          ? lookups.vehicles
          : [{ vehicleId: row.vehicleId, unitNumber: row.unitNumber || "(current)" }, ...lookups.vehicles];

        return (
          <Select
            value={row.vehicleId || 0}
            onChange={(e) => {
              const vehicleId = Number(e.target.value);
              const picked = options.find((v) => v.vehicleId === vehicleId);
              updateRow(row.yardDailyId, { vehicleId, unitNumber: picked?.unitNumber ?? "" });
            }}
            disabled={!isRowEditable(row)}
            variant="standard"
            sx={{ width: "100%", fontSize: ".85em" }}
          >
            <MenuItem value={0}>(none)</MenuItem>
            {options.map((vehicle) => (
              <MenuItem key={vehicle.vehicleId} value={vehicle.vehicleId} sx={{ fontSize: ".85em" }}>
                {vehicle.unitNumber}
              </MenuItem>
            ))}
          </Select>
        );
      },
    },
    {
      field: "controlNumber",
      headerName: "Control Number",
      width: 170,
      sortable: false,
      renderCell: (params) => {
        const row = params.row as ShiftGridRow;
        return textCell(row, row.controlNumber, (next) => updateRow(row.yardDailyId, { controlNumber: next }), 160);
      },
    },
    {
      field: "shiftName",
      headerName: "Shift Name / Schedule",
      width: 190,
      sortable: false,
      renderCell: (params) => {
        const row = params.row as ShiftGridRow;
        return (
          // variant="inherit" takes the grid's own font size; anything else compounds with
          // the .85em set on the DataGrid and renders smaller than the neighbouring columns.
          <Stack>
            <Typography variant="inherit">
              {row.shiftName}
              {row.isSplitShift && <sup>*</sup>}
            </Typography>
            <Typography variant="inherit" sx={{ color: "text.secondary" }}>
              {row.masterTimes}
            </Typography>
          </Stack>
        );
      },
    },
    {
      field: "shiftDateAdjust",
      headerName: "Date Adjust",
      width: 100,
      sortable: false,
      renderCell: (params) => {
        const row = params.row as ShiftGridRow;
        return (
          <Select
            value={row.shiftDateAdjust || "0"}
            onChange={(e) => updateRow(row.yardDailyId, { shiftDateAdjust: e.target.value })}
            disabled={!isRowEditable(row)}
            variant="standard"
            sx={{ width: "100%", fontSize: ".85em" }}
          >
            <MenuItem value="0">0</MenuItem>
            <MenuItem value="-1">-1</MenuItem>
            <MenuItem value="+1">+1</MenuItem>
          </Select>
        );
      },
    },
    {
      field: "invoiceStart",
      headerName: "Invoice Start",
      width: 130,
      sortable: false,
      renderCell: (params) => {
        const row = params.row as ShiftGridRow;
        return (
          <Stack sx={{ width: "100%" }}>
            {textCell(row, row.dateStartInvoice, (next) => updateRow(row.yardDailyId, { dateStartInvoice: next }), 110)}
            {textCell(row, row.timeStartInvoice, (next) => updateRow(row.yardDailyId, { timeStartInvoice: next }), 110)}
          </Stack>
        );
      },
    },
    {
      field: "invoiceEnd",
      headerName: "Invoice End",
      width: 130,
      sortable: false,
      renderCell: (params) => {
        const row = params.row as ShiftGridRow;
        return (
          <Stack sx={{ width: "100%" }}>
            {textCell(row, row.dateEndInvoice, (next) => updateRow(row.yardDailyId, { dateEndInvoice: next }), 110)}
            {textCell(row, row.timeEndInvoice, (next) => updateRow(row.yardDailyId, { timeEndInvoice: next }), 110)}
          </Stack>
        );
      },
    },
    {
      field: "payrollStart",
      headerName: "Payroll Start",
      width: 130,
      sortable: false,
      renderCell: (params) => {
        const row = params.row as ShiftGridRow;
        return (
          <Stack sx={{ width: "100%" }}>
            {textCell(row, row.dateStartPayroll, (next) => updateRow(row.yardDailyId, { dateStartPayroll: next }), 110)}
            {textCell(row, row.timeStartPayroll, (next) => updateRow(row.yardDailyId, { timeStartPayroll: next }), 110)}
          </Stack>
        );
      },
    },
    {
      field: "payrollEnd",
      headerName: "Payroll End",
      width: 130,
      sortable: false,
      renderCell: (params) => {
        const row = params.row as ShiftGridRow;
        return (
          <Stack sx={{ width: "100%" }}>
            {textCell(row, row.dateEndPayroll, (next) => updateRow(row.yardDailyId, { dateEndPayroll: next }), 110)}
            {textCell(row, row.timeEndPayroll, (next) => updateRow(row.yardDailyId, { timeEndPayroll: next }), 110)}
          </Stack>
        );
      },
    },
    {
      field: "timecardHours",
      headerName: "Timecard",
      width: 85,
      sortable: false,
      align: "right",
      headerAlign: "right",
      renderCell: (params) => <span>{(params.row as ShiftGridRow).timecardHours}</span>,
    },
    {
      field: "sourceDataName",
      headerName: "Data Source",
      width: 120,
      sortable: false,
      renderCell: (params) => {
        const row = params.row as ShiftGridRow;
        return (
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {row.hasSourceData ? row.sourceDataName : ""}
          </Typography>
        );
      },
    },
    {
      field: "sourceTimeStart",
      headerName: "Start Time",
      width: 90,
      sortable: false,
      align: "right",
      headerAlign: "right",
      renderCell: (params) => {
        const row = params.row as ShiftGridRow;
        return (
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {row.sourceTimeStart}
          </Typography>
        );
      },
    },
    {
      field: "sourceTimeEnd",
      headerName: "End Time",
      width: 90,
      sortable: false,
      align: "right",
      headerAlign: "right",
      renderCell: (params) => {
        const row = params.row as ShiftGridRow;
        return (
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {row.sourceTimeEnd}
          </Typography>
        );
      },
    },
    {
      field: "tripsRan",
      headerName: "# Trips",
      width: 80,
      sortable: false,
      renderCell: (params) => {
        const row = params.row as ShiftGridRow;
        return withSource(
          row,
          textCell(row, String(row.tripsRan ?? 0), (next) => updateRow(row.yardDailyId, { tripsRan: toInt(next) }), 60, true),
          row.sourceShiftTripCount
        );
      },
    },
    {
      field: "odometerBeg",
      headerName: "Odom Begin",
      width: 105,
      sortable: false,
      renderCell: (params) => {
        const row = params.row as ShiftGridRow;
        return withSource(
          row,
          textCell(row, String(row.odometerBeg ?? 0), (next) => updateRow(row.yardDailyId, { odometerBeg: toInt(next) }), 85, true),
          row.sourceOdometerBeg
        );
      },
    },
    {
      field: "odometerEnd",
      headerName: "Odom End",
      width: 105,
      sortable: false,
      renderCell: (params) => {
        const row = params.row as ShiftGridRow;
        return withSource(
          row,
          textCell(row, String(row.odometerEnd ?? 0), (next) => updateRow(row.yardDailyId, { odometerEnd: toInt(next) }), 85, true),
          row.sourceOdometerEnd
        );
      },
    },
    {
      field: "miles",
      headerName: "Miles",
      width: 85,
      sortable: false,
      align: "right",
      headerAlign: "right",
      renderCell: (params) => {
        const row = params.row as ShiftGridRow;
        return withSource(
          row,
          // A negative figure means the odometers are the wrong way round.
          <Typography variant="inherit" sx={{ textAlign: "right", color: row.miles < 0 ? "error.main" : "inherit" }}>
            {row.miles}
          </Typography>,
          row.sourceShiftMiles
        );
      },
    },
    {
      field: "locMgrReview",
      headerName: "Mgr Review",
      width: 90,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => <Checkbox checked={(params.row as ShiftGridRow).locMgrReview} disabled size="small" />,
    },
    {
      field: "autoApproveError",
      headerName: "Auto",
      width: 60,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        const row = params.row as ShiftGridRow;
        if (row.autoApproveError) {
          return (
            <Tooltip title={`Auto approve failure:\n${row.autoApproveError.split(" | ").join("\n")}`}>
              <ErrorOutlineIcon color="error" fontSize="small" />
            </Tooltip>
          );
        }
        if (row.approved && !row.approvedBy) {
          return (
            <Tooltip title="Auto approved">
              <CheckCircleOutlineIcon color="success" fontSize="small" />
            </Tooltip>
          );
        }
        return null;
      },
    },
    {
      field: "approved",
      headerName: "Approved",
      width: 90,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        const row = params.row as ShiftGridRow;
        return (
          // Approving is its own permission, not part of general edit rights: someone may be
          // trusted to correct a shift without being the one who signs it off for payroll.
          <Checkbox
            checked={row.approved}
            disabled={!row.isEditable || !permissions.canApprove}
            size="small"
            onChange={(e) => updateRow(row.yardDailyId, { approved: e.target.checked })}
          />
        );
      },
    },
    {
      field: "isCanceled",
      headerName: "Cancel",
      width: 80,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        const row = params.row as ShiftGridRow;
        return (
          <Checkbox
            checked={row.isCanceled}
            disabled={!isRowEditable(row)}
            size="small"
            onChange={(e) => updateRow(row.yardDailyId, { isCanceled: e.target.checked })}
          />
        );
      },
    },
    {
      field: "cancelReason",
      headerName: "Cancel / Overage Reason",
      width: 200,
      sortable: false,
      renderCell: (params) => {
        const row = params.row as ShiftGridRow;
        const grouped: ShiftReasonOption[] = lookups.cancelReasons;
        return (
          <Select
            value={row.cancelReason || ""}
            onChange={(e) => updateRow(row.yardDailyId, { cancelReason: e.target.value })}
            disabled={!isRowEditable(row)}
            variant="standard"
            displayEmpty
            sx={{ width: "100%", fontSize: ".85em" }}
          >
            <MenuItem value="">(none)</MenuItem>
            {grouped.map((reason) => (
              <MenuItem key={`${reason.reasonType}-${reason.description}`} value={reason.description} sx={{ fontSize: ".85em" }}>
                {reason.description}
                {reason.reasonType === "Overage" ? " (overage)" : ""}
              </MenuItem>
            ))}
          </Select>
        );
      },
    },
    {
      field: "notes",
      headerName: "Notes for Client",
      width: 190,
      sortable: false,
      renderCell: (params) => {
        const row = params.row as ShiftGridRow;
        return textCell(row, row.notes, (next) => updateRow(row.yardDailyId, { notes: next.slice(0, 100) }), 180);
      },
    },
    {
      field: "approvedBy",
      headerName: "Approved By",
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Typography variant="caption">{(params.row as ShiftGridRow).approvedBy}</Typography>
      ),
    },
    {
      field: "sourceCreatedBy",
      headerName: "Created By",
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Typography variant="caption">{(params.row as ShiftGridRow).sourceCreatedBy}</Typography>
      ),
    },
  ];
};

const toInt = (value: string): number => {
  const digits = (value ?? "").replace(/[^0-9]/g, "");
  return digits === "" ? 0 : parseInt(digits, 10);
};

export default ShiftUpdate;
